// web/src/components/playback/PlaybackBar.tsx
//
// Renders only when playbackMode === 'playback'. Reads history min/max
// timestamps from positionHistory (derived, not in slice).

import React from 'react'
import clsx from 'clsx'
import { useAppDispatch } from '../../app/useAppDispatch'
import { useAppSelector } from '../../app/useAppSelector'
import { rtlsActions } from '@shared/slices/rtlsSlice'
import { useHistoryBounds } from '../../scene/rtls/useHistoryBounds'

function formatTimestamp(ts: number): string {
  const d = new Date(ts)
  return d.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

export const PlaybackBar: React.FC = () => {
  const dispatch = useAppDispatch()
  const playbackTs      = useAppSelector(s => s.rtls.playbackTimestamp)
  const playbackSpeed   = useAppSelector(s => s.rtls.playbackSpeed)
  const playbackPlaying = useAppSelector(s => s.rtls.playbackPlaying)

  // Scrubber bounds follow the selected asset's trail when one is selected;
  // fall back to the full fleet window otherwise. See useHistoryBounds.
  const { minTs, maxTs } = useHistoryBounds()

  const currentTs = playbackTs ?? minTs
  const range = maxTs - minTs
  const progress = range > 0 ? (currentTs - minTs) / range : 0

  const handleScrub = (e: React.ChangeEvent<HTMLInputElement>) => {
    const pct = Number(e.target.value) / 1000
    dispatch(rtlsActions.setPlaybackTimestamp(Math.round(minTs + pct * range)))
    // Pause auto-play when the user drags the scrubber manually.
    dispatch(rtlsActions.setPlaybackPlaying(false))
  }

  const handlePlayPause = () => {
    // If at the end, restart before playing.
    if (playbackPlaying === false && currentTs >= maxTs) {
      dispatch(rtlsActions.setPlaybackTimestamp(minTs))
    }
    dispatch(rtlsActions.setPlaybackPlaying(!playbackPlaying))
  }

  const handleRestart = () => {
    dispatch(rtlsActions.setPlaybackTimestamp(minTs))
    dispatch(rtlsActions.setPlaybackPlaying(false))
  }

  const handleLive = () => {
    dispatch(rtlsActions.setPlaybackMode('live'))
  }

  const MIN_SPEED = 1
  const MAX_SPEED = 50
  // Below 10x, step by 1 (fine control). At 10x and above, step by 5 so the
  // stepper doesn't require 40 clicks to reach 50.
  const stepSize = (base: number, dir: 1 | -1): number => {
    if (dir === 1) return base < 10 ? 1 : 5
    return base <= 10 ? 1 : 5
  }
  const handleSpeedStep = (dir: 1 | -1) => {
    const step = stepSize(playbackSpeed, dir) * dir
    dispatch(rtlsActions.setPlaybackSpeed(playbackSpeed + step))
  }

  const btn = 'flex h-8 items-center justify-center rounded-sm text-textPrimary transition-colors'
  const iconBtn = clsx(btn, 'w-8 text-lg leading-none hover:bg-surfaceLight')

  return (
    <div
      className="absolute bottom-0 left-0 right-0 z-20 flex h-bottom-bar items-center gap-sm border-t border-surfaceBorder bg-surface/95 px-lg backdrop-blur-sm"
      role="toolbar"
      aria-label="Playback controls"
    >
      {/* Restart */}
      <button type="button" onClick={handleRestart} title="Restart from beginning" className={iconBtn}>
        ⏮
      </button>

      {/* Play / Pause */}
      <button
        type="button"
        onClick={handlePlayPause}
        title={playbackPlaying ? 'Pause' : 'Play'}
        className={iconBtn}
      >
        {playbackPlaying ? '⏸' : '▶'}
      </button>

      {/* Speed stepper: 1x–10x */}
      <div className="flex items-center gap-xs" role="group" aria-label="Playback speed">
        <button
          type="button"
          onClick={() => handleSpeedStep(-1)}
          disabled={playbackSpeed <= MIN_SPEED}
          title="Slower"
          aria-label="Decrease playback speed"
          className={clsx(
            'h-7 w-7 rounded-sm text-base font-semibold leading-none transition-colors',
            'text-textMuted hover:bg-surfaceLight hover:text-textPrimary',
            'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent',
          )}
        >
          −
        </button>
        <span className="min-w-[32px] select-none text-center text-xs font-semibold tabular-nums text-textPrimary">
          {playbackSpeed}x
        </span>
        <button
          type="button"
          onClick={() => handleSpeedStep(1)}
          disabled={playbackSpeed >= MAX_SPEED}
          title="Faster"
          aria-label="Increase playback speed"
          className={clsx(
            'h-7 w-7 rounded-sm text-base font-semibold leading-none transition-colors',
            'text-textMuted hover:bg-surfaceLight hover:text-textPrimary',
            'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent',
          )}
        >
          +
        </button>
      </div>

      {/* Scrubber */}
      <input
        type="range"
        min={0}
        max={1000}
        value={Math.round(progress * 1000)}
        onChange={handleScrub}
        className="flex-1 accent-primary"
        aria-label="Playback position"
      />

      {/* Timestamp */}
      <span className="min-w-[80px] text-right text-xs tabular-nums text-textMuted">
        {formatTimestamp(currentTs)}
      </span>

      {/* Live exit */}
      <button
        type="button"
        onClick={handleLive}
        className="rounded-sm bg-statusGreen/20 px-sm py-xs text-xs font-semibold text-statusGreen hover:bg-statusGreen/30"
        title="Return to live view"
      >
        Live
      </button>
    </div>
  )
}
